import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Button, Navbar, Nav } from 'react-bootstrap';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { User, authService, friendshipService, loadUnreadNotificationCount } from '../services/api';
import FriendsList from './FriendsList';
import PendingRequests from './PendingRequests';
import LanguageSelector from './LanguageSelector';
import ProfileAchievementsSection from './ProfileAchievementsSection';
import NavbarBrandLogo from './NavbarBrandLogo';
import UserNavbarMenus from './UserNavbarMenus';
import NotificationBell from './NotificationBell';
import NavbarMusicToggle from './NavbarMusicToggle';
import PwaInstallButton from './PwaInstallButton';
import { isVipSubscriber } from '../utils/isVipSubscriber';
import './UserProfile.css';
import './PublicProfile.css';

const UserProfile: React.FC = () => {
  const { t } = useSafeTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [showPendingRequests, setShowPendingRequests] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');

  useEffect(() => {
    const loadUserData = async () => {
      if (token) {
        localStorage.setItem('authToken', token);
      }

      try {
        const userData = await authService.getProfile();
        setUser(userData);
        setUnreadNotificationCount(await loadUnreadNotificationCount(userData));
        localStorage.setItem('user', JSON.stringify(userData));
      } catch {
        /* ignore */
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [token]);

  useEffect(() => {
    if (user) {
      loadPendingRequestsCount();
    }
  }, [user]);

  const loadPendingRequestsCount = async () => {
    try {
      const requests = await friendshipService.getPendingRequests();
      setPendingRequestsCount(requests.length);
    } catch {
      /* ignore */
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    window.location.href = '/';
  };

  const handleRequestResponded = () => {
    loadPendingRequestsCount();
  };

  const getUserTypeBadge = (userType: string | number) => {
    switch (userType) {
      case 'Admin':
      case 3:
        return <Badge bg="danger">Admin</Badge>;
      case 'Vip':
      case 'Premium':
      case 2:
        return <Badge bg="warning">VIP</Badge>;
      case 'Free':
      case 1:
        return <Badge bg="secondary">Free</Badge>;
      default:
        return <Badge bg="secondary">Free</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="profile-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-loading">
        <div className="text-center">
          <p>{t('user.userNotFound', 'User not found. Redirecting to login...')}</p>
        </div>
      </div>
    );
  }

  const displayName = user.nickname || user.name;

  return (
    <div className="profile-page">
      <Navbar bg="white" expand="lg" className="home-navbar" collapseOnSelect>
        <Container fluid>
          <Navbar.Brand
            className="navbar-brand d-flex align-items-center"
            onClick={() => (window.location.href = '/')}
            style={{ cursor: 'pointer' }}
            aria-label="Quizter Club"
          >
            <NavbarBrandLogo />
            <span className="d-none d-lg-inline">Quizter Club</span>
          </Navbar.Brand>

          <div className="d-flex d-lg-none align-items-center ms-auto gap-1 gap-sm-2 navbar-mobile-toolbar">
            <PwaInstallButton className="flex-shrink-0" iconOnly />
            <LanguageSelector user={user} isInline={true} />
            <NavbarMusicToggle className="flex-shrink-0" />
            <NotificationBell
              className="d-lg-none"
              unreadCount={unreadNotificationCount}
              onUnreadCountChange={setUnreadNotificationCount}
            />
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
          </div>
          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-lg-end">
            <Nav className="flex-column flex-lg-row align-items-stretch align-items-lg-center w-100 justify-content-lg-end">
              <div className="d-none d-lg-flex align-items-center gap-2 me-lg-3">
                <PwaInstallButton />
                <LanguageSelector user={user} isInline={true} />
              </div>
              <UserNavbarMenus
                user={user}
                pendingRequestsCount={pendingRequestsCount}
                unreadNotificationCount={unreadNotificationCount}
                onUnreadNotificationCountChange={setUnreadNotificationCount}
                onOpenFriends={() => setShowFriendsList(true)}
                onOpenPending={() => setShowPendingRequests(true)}
                onLogout={handleLogout}
              />
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container fluid className="home-content">
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6}>
            <Card className="profile-card">
              <Card.Body className="text-center p-4">
                <div className="profile-avatar mb-4">
                  <i className="bi bi-person-circle"></i>
                </div>

                <h2
                  className={`profile-nickname mb-3${
                    isVipSubscriber(user.userType) ? ' vip-subscriber-nickname' : ''
                  }`}
                >
                  {displayName}
                  {isVipSubscriber(user.userType) && (
                    <i className="bi bi-gem text-warning ms-1" title="VIP" aria-hidden />
                  )}
                </h2>

                <div className="profile-badges mb-4">{getUserTypeBadge(user.userType)}</div>

                <div className="profile-stats">
                  <Row className="g-2 profile-stats-row">
                    <Col xs={6}>
                      <div className="stat-item">
                        <div className="stat-value">{user.level}</div>
                        <div className="stat-label">{t('user.level', 'Level')}</div>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="stat-item">
                        <div className="stat-value">{user.experiencePoints.toLocaleString()}</div>
                        <div className="stat-label">{t('user.experiencePoints', 'Experience Points')}</div>
                      </div>
                    </Col>
                  </Row>
                </div>

                <ProfileAchievementsSection
                  variant="mine"
                  className="mt-3"
                  sharePreviewPlayerName={displayName}
                />

                <div className="d-flex gap-3 justify-content-center flex-wrap mt-4">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate('/settings')}
                  >
                    <i className="bi bi-gear me-2"></i>
                    {t('common.settings', 'Settings')}
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => navigate('/')}>
                    <i className="bi bi-arrow-left me-2"></i>
                    {t('navigation.backToHome', 'Back to Home')}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {showFriendsList && <FriendsList onClose={() => setShowFriendsList(false)} />}

      {showPendingRequests && (
        <PendingRequests
          onClose={() => setShowPendingRequests(false)}
          onRequestResponded={handleRequestResponded}
        />
      )}
    </div>
  );
};

export default UserProfile;
